/**
 * OAuth Callback Page
 *
 * Handles the Box OAuth redirect (connect flow and export-to-Box flow) and the
 * Dropbox connect redirect.
 *
 * The provider is decided by the callback path, not by a `state` parameter:
 * each provider is registered with its own redirect URI, so `/auth/box/callback`
 * and `/auth/dropbox/callback` are already unforgeable-by-accident and there is
 * nothing for a caller to substitute.
 *
 * `state` decides something else entirely, and it is not cosmetic. This page
 * runs inside the user's authenticated session, so whatever authorization code
 * lands here gets exchanged with the firm's own credentials. Being behind auth
 * is no protection: an attacker who authorizes **their own** Box or Dropbox
 * account and sends a firm's user the resulting callback URL would have that
 * user's own browser bind the attacker's cloud account to the firm — and T4
 * then freezes it in place. So the backend issues a single-use nonce when it
 * builds each authorize URL, and refuses any callback that does not carry it
 * back. This page's job is to forward it and to fail closed when it is absent:
 * `state` is a required argument here, never an "if present" extra.
 *
 * Its prefix (`connect.` / `export.`) is a routing hint only — which endpoint to
 * post to, since two flows share Box's redirect URI. The backend reads the real
 * purpose off its own row.
 */

import React, { useEffect, useRef, useState } from 'react';
import { friendlyErrorMessage } from '../utils/apiErrorDetail';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  authenticateBox,
  authenticateDropbox,
  authenticateSharePoint,
  extractAuthCodeFromUrl,
  extractOAuthStateFromUrl,
  oauthStatePurpose,
  syncBoxAccount
} from '../services/integrationApi';
import { completeBoxExportOAuth } from '../services/fileApi';
import { Colors } from '../utils/colors';

const OAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');
  const location = useLocation();
  const navigate = useNavigate();
  const processedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const fullUrl = window.location.href;
    // React Strict Mode replays effects in development. OAuth codes and state
    // nonces are single-use, so replaying this request creates a false failure.
    if (processedUrlRef.current === fullUrl) return;
    processedUrlRef.current = fullUrl;

    const handleOAuthCallback = async () => {
      try {
        const pathname = location.pathname;

        const provider = pathname.includes('/auth/dropbox/callback')
          ? ('dropbox' as const)
          : pathname.includes('/auth/sharepoint/callback')
          ? ('sharepoint' as const)
          : ('box' as const);
        if (
          !pathname.includes('/auth/box/callback') &&
          !pathname.includes('/auth/dropbox/callback') &&
          !pathname.includes('/auth/sharepoint/callback')
        ) {
          throw new Error('Unknown OAuth provider');
        }

        // Extract auth code from URL
        // Never logged: the callback URL carries the single-use authorization
        // code, and console output is one copy of it too many.
        const authCode = extractAuthCodeFromUrl(fullUrl, provider);
        if (!authCode) {
          throw new Error('No authorization code found in URL');
        }

        // Read before any exchange, and refused before any exchange. An
        // unrecognised or absent state is not a degraded case to work around —
        // every authorize URL this app builds carries one, so its absence means
        // the flow started somewhere else.
        const state = extractOAuthStateFromUrl(fullUrl);
        const purpose = oauthStatePurpose(state);
        if (!state || !purpose) {
          throw new Error(
            'This connection link did not come from Doppler. Start the connection again from the Data Sources page.'
          );
        }

        if (provider === 'dropbox') {
          setMessage('Authenticating with Dropbox...');
          // Code exchanged server-side; the grant lands in the firm's
          // cloud_accounts row and nothing token-shaped comes back here.
          const dropboxResult = await authenticateDropbox(authCode, state);
          setStatus('success');
          // Deliberately not "downloading files". Dropbox's sync is a scheduled
          // job outside the app, so nothing lands during this request — telling
          // the admin otherwise would have them refresh an empty deal room
          // looking for files that are not due yet.
          setMessage(
            'Dropbox connected. Your files will appear after the next scheduled sync.'
          );
          setTimeout(() => {
            navigate('/', {
              state: {
                connectedProvider: provider,
                userInfo: dropboxResult.user_info,
              },
            });
          }, 2000);
          return;
        }

        if (provider === 'sharepoint') {
          setMessage('Authenticating with Microsoft...');
          const sharepointResult = await authenticateSharePoint(authCode, state);
          setStatus('success');
          setMessage(
            'SharePoint connected. Your files will appear after the next scheduled sync.'
          );
          setTimeout(() => {
            navigate('/', {
              state: {
                connectedProvider: provider,
                userInfo: sharepointResult.user_info,
              },
            });
          }, 2000);
          return;
        }

        if (purpose === 'export') {
          setMessage('Connecting Box and starting export...');
          const exportResult = await completeBoxExportOAuth(authCode, state);
          sessionStorage.setItem('box_export_job_id', exportResult.job_id);
          if (window.opener && window.opener !== window) {
            window.opener.postMessage(
              { type: 'box-export-started', jobId: exportResult.job_id },
              window.location.origin,
            );
          }
          setStatus('success');
          setMessage('Export to Box started.');
          setTimeout(() => {
            if (window.opener && window.opener !== window) {
              window.close();
            } else {
              navigate('/chat');
            }
          }, 1000);
          return;
        }

        setMessage('Authenticating with Box...');

        // The code is exchanged server-side and the resulting grant is stored
        // against the firm's account. Nothing token-shaped comes back here.
        const result = await authenticateBox(authCode, state);

        // Start immediately, but do not hold the callback page open while the
        // account downloads. The scheduled worker retries failed/incomplete runs.
        void syncBoxAccount().catch((error) =>
          console.error('Box background sync failed:', error)
        );
        setStatus('success');
        setMessage('Box connected. Your files are syncing in the background.');

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/', {
              state: {
                connectedProvider: provider,
                userInfo: result.user_info
              }
          });
        }, 2000);

      } catch (error) {
        console.error('OAuth callback error:', error);
        if (window.opener && window.opener !== window) {
          window.opener.postMessage(
            {
              type: 'box-export-error',
              error: friendlyErrorMessage(error, 'Authentication failed'),
            },
            window.location.origin,
          );
        }
        setStatus('error');
        setMessage(
          friendlyErrorMessage(
            error,
            "We couldn't finish connecting that account. Please try again.",
          ),
        );

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          if (window.opener && window.opener !== window) {
            window.close();
          } else {
            navigate('/');
          }
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-auto">
        <div
          className="bg-white rounded-lg shadow-md p-8 text-center"
          style={{ borderColor: Colors.BORDER_LIGHT }}
        >
          {/* Status Icon */}
          <div className="mb-6">
            {status === 'loading' && (
              <div className="mx-auto w-12 h-12 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            )}
            {status === 'success' && (
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
            {status === 'error' && (
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
          </div>

          {/* Status Message */}
          <h2
            className="text-xl font-semibold mb-4"
            style={{ color: Colors.TEXT_PRIMARY }}
          >
            {status === 'loading' && 'Connecting...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Connection Failed'}
          </h2>

          <p
            className="text-sm"
            style={{ color: Colors.TEXT_SECONDARY }}
          >
            {message}
          </p>

          {status !== 'loading' && (
            <div className="mt-6">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 rounded-md text-white text-sm font-medium"
                style={{ backgroundColor: Colors.PRIMARY }}
              >
                Return to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/*
 * `isExportToBoxState` used to live here: it base64-decoded the state and
 * believed whatever `action` it found. That made the state a *message from the
 * caller* rather than a token from the server, which is why it could not tell an
 * authorization this user started from one an attacker started. Routing now
 * reads a prefix and the backend verifies its own row — see `oauthStatePurpose`.
 */

export default OAuthCallback;
